import { uniq } from 'lodash';

import { TabModules, UseModulesConfigOutput } from '~/data/company';
import { Modules } from '~/data/modules';
import { DevelopmentTabs, PeopleTabs, RewardingTabs } from '~/data/tabs';
import {
  AdminWorkspaceRoles,
  Roles,
  getRoleTranslationKey,
} from '~/data/user/role';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';

export interface WidgetProps<Config extends {} = {}> {
  title?: string;
  config?: Config & {
    role?: AdminWorkspaceRoles;
    workspaceType?: WorkspaceType;
  };
  width: number;
}

export enum MyWorkspaceWidgetNames {
  AnnouncementsWidget = 'AnnouncementsWidget',
  MyTeamWidget = 'MyTeamWidget',
  ApprovalWidget = 'ApprovalWidget',
  TeamAbsencesTodayWidget = 'TeamAbsencesTodayWidget',
  TeamCompensationWidget = 'TeamCompensationWidget',
  TeamPerformanceWidget = 'TeamPerformanceWidget',
  MyTeamAbsencesWidget = 'MyTeamAbsencesWidget',
  TeamTimeManagementWidget = 'TeamTimeManagementWidget',
  TeamTrainingsWidget = 'TeamTrainingsWidget',
  TeamTasksWidget = 'TeamTasksWidget',
  TeamAnniversariesWidget = 'TeamAnniversariesWidget',
  MyDataWidget = 'MyDataWidget',
  MyAbsencesWidget = 'MyAbsencesWidget',
  MyCompensationWidget = 'MyCompensationWidget',
  MyPerformanceWidget = 'MyPerformanceWidget',
  MyNotificationsWidget = 'MyNotificationsWidget',
  MyTasksWidget = 'MyTasksWidget',
  QuickLinksWidget = 'QuickLinksWidget',
  MyWorklistsWidget = 'MyWorklistsWidget',
  MyTeamWorklistsWidget = 'MyTeamWorklistsWidget',
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // SolaIntelWidget = 'SolaIntelWidget',
}

/** Widgets by Workspace Roles **/

export const getWorkspaceRoleWidgets = (
  role: AdminWorkspaceRoles,
): MyWorkspaceWidgetNames[] => {
  const employeeRoleWidgets = [
    MyWorkspaceWidgetNames.AnnouncementsWidget,
    MyWorkspaceWidgetNames.QuickLinksWidget,
    MyWorkspaceWidgetNames.MyDataWidget,
    MyWorkspaceWidgetNames.MyAbsencesWidget,
    MyWorkspaceWidgetNames.MyCompensationWidget,
    MyWorkspaceWidgetNames.MyPerformanceWidget,
    MyWorkspaceWidgetNames.MyNotificationsWidget,
    MyWorkspaceWidgetNames.MyTasksWidget,
    MyWorkspaceWidgetNames.MyWorklistsWidget,
  ];

  const managerWidgets = [
    MyWorkspaceWidgetNames.AnnouncementsWidget,
    MyWorkspaceWidgetNames.ApprovalWidget,
    MyWorkspaceWidgetNames.QuickLinksWidget,
    MyWorkspaceWidgetNames.MyTeamWidget,
    MyWorkspaceWidgetNames.TeamAbsencesTodayWidget,
    MyWorkspaceWidgetNames.TeamCompensationWidget,
    MyWorkspaceWidgetNames.TeamPerformanceWidget,
    MyWorkspaceWidgetNames.MyTeamAbsencesWidget,
    MyWorkspaceWidgetNames.TeamTimeManagementWidget,
    MyWorkspaceWidgetNames.TeamTrainingsWidget,
    MyWorkspaceWidgetNames.TeamTasksWidget,
    MyWorkspaceWidgetNames.TeamAnniversariesWidget,
    MyWorkspaceWidgetNames.MyTeamWorklistsWidget,
    /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
    // MyWorkspaceWidgetNames.SolaIntelWidget,
  ];

  const alternateManagerWidgets = managerWidgets.filter(
    (w) => ![MyWorkspaceWidgetNames.MyTeamWorklistsWidget].includes(w),
  );

  const teamManagerWidgets = alternateManagerWidgets.filter(
    (w) =>
      ![
        MyWorkspaceWidgetNames.TeamCompensationWidget,
        MyWorkspaceWidgetNames.TeamTimeManagementWidget,
        /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
        // MyWorkspaceWidgetNames.SolaIntelWidget,
      ].includes(w),
  );

  switch (role) {
    case AdminWorkspaceRoles.HR_ADMIN:
    case AdminWorkspaceRoles.HR_STAFF:
      return managerWidgets;
    case AdminWorkspaceRoles.MANAGER:
      return managerWidgets;
    case AdminWorkspaceRoles.ALT_MANAGER:
      return alternateManagerWidgets;
    case AdminWorkspaceRoles.DEPUTY_MANAGER:
      return teamManagerWidgets;
    case AdminWorkspaceRoles.EMPLOYEE:
    default:
      return employeeRoleWidgets;
  }
};

export const getWorkspaceRolesWidgets = (
  roles: AdminWorkspaceRoles[],
): MyWorkspaceWidgetNames[] => {
  return uniq(roles.map(getWorkspaceRoleWidgets).flat());
};

export const filterWorkspaceRoleEnabledWidgets = (props: {
  widgets: MyWorkspaceWidgetNames[];
  workspaceRoles: AdminWorkspaceRoles[];
}): MyWorkspaceWidgetNames[] => {
  const { widgets, workspaceRoles } = props;

  const rolesWidgets = getWorkspaceRolesWidgets(workspaceRoles);

  return widgets.filter((r) => rolesWidgets.includes(r));
};

/** Filter Enabled widgets **/

export const filterFeatureEnabledWidgets = (
  props: {
    widgets: MyWorkspaceWidgetNames[];
    workspaceRoles: AdminWorkspaceRoles[];
  } & Pick<UseModulesConfigOutput, 'isModuleEnabled' | 'isModuleTabEnabled'>,
): MyWorkspaceWidgetNames[] => {
  const { widgets, workspaceRoles, isModuleEnabled, isModuleTabEnabled } =
    props;

  const rewardingAccessRoles = Object.values(AdminWorkspaceRoles).filter(
    (r) =>
      ![
        AdminWorkspaceRoles.EMPLOYEE,
        AdminWorkspaceRoles.DEPUTY_MANAGER,
      ].includes(r),
  );
  const isRewardingAccessRole = workspaceRoles.some((r) =>
    rewardingAccessRoles.includes(r),
  );

  // Enabled states
  const isAbsencesEnabled = isModuleTabEnabled(
    TabModules.PEOPLE,
    PeopleTabs.Absences,
  );

  const isWorkTimeEnabled =
    isModuleEnabled(Modules.WORK_TIME_REPORTING) &&
    isModuleTabEnabled(TabModules.REWARDING, RewardingTabs.WorkTime);

  const isRewardingGeneralEnabled = isModuleTabEnabled(
    TabModules.REWARDING,
    RewardingTabs.General,
  );

  const isTrainingsEnabled = isModuleTabEnabled(
    TabModules.DEVELOPMENT,
    DevelopmentTabs.Education,
  );

  return widgets.filter((widget) => {
    // Filter by Enabled Modules & Tabs dependencies
    switch (widget) {
      case MyWorkspaceWidgetNames.MyCompensationWidget:
        return isRewardingGeneralEnabled;
      case MyWorkspaceWidgetNames.TeamCompensationWidget:
        return isRewardingAccessRole && isRewardingGeneralEnabled;

      case MyWorkspaceWidgetNames.TeamTimeManagementWidget:
        return isWorkTimeEnabled;

      case MyWorkspaceWidgetNames.MyTasksWidget:
      case MyWorkspaceWidgetNames.TeamTasksWidget:
      case MyWorkspaceWidgetNames.MyWorklistsWidget:
      case MyWorkspaceWidgetNames.MyTeamWorklistsWidget:
        return isModuleEnabled(Modules.COLLABORATION);

      case MyWorkspaceWidgetNames.MyPerformanceWidget:
      case MyWorkspaceWidgetNames.TeamPerformanceWidget:
        return isModuleEnabled(Modules.PERFORMANCE);

      case MyWorkspaceWidgetNames.MyAbsencesWidget:
      case MyWorkspaceWidgetNames.MyTeamAbsencesWidget:
      case MyWorkspaceWidgetNames.TeamAbsencesTodayWidget:
        return isAbsencesEnabled;

      case MyWorkspaceWidgetNames.ApprovalWidget:
        return (
          isAbsencesEnabled ||
          isTrainingsEnabled ||
          (isRewardingAccessRole && isModuleEnabled(Modules.REWARDING))
        );

      case MyWorkspaceWidgetNames.TeamTrainingsWidget:
        return isTrainingsEnabled;

      case MyWorkspaceWidgetNames.MyDataWidget:
      case MyWorkspaceWidgetNames.AnnouncementsWidget:
      case MyWorkspaceWidgetNames.MyNotificationsWidget:
      case MyWorkspaceWidgetNames.TeamAnniversariesWidget:
      case MyWorkspaceWidgetNames.QuickLinksWidget:
      case MyWorkspaceWidgetNames.MyTeamWidget:
      default:
        return true;
    }
  });
};

export const getAvailableWidgets = (
  props: {
    workspaceRoles: AdminWorkspaceRoles[];
  } & Pick<UseModulesConfigOutput, 'isModuleEnabled' | 'isModuleTabEnabled'>,
): MyWorkspaceWidgetNames[] => {
  const { workspaceRoles, isModuleEnabled, isModuleTabEnabled } = props;

  // Get Widgets by Roles & Enabled modules
  return filterFeatureEnabledWidgets({
    widgets: getWorkspaceRolesWidgets(workspaceRoles),
    workspaceRoles,
    isModuleEnabled,
    isModuleTabEnabled,
  });
};

export const getUserRoleByWorkspaceRole = (
  role: AdminWorkspaceRoles,
): Roles | undefined => {
  return Object.values(Roles).find((r) => r.toString() === role.toString());
};

export const getWorkspaceRoleTranslationKey = (
  role: AdminWorkspaceRoles,
): string => {
  const userRole = getUserRoleByWorkspaceRole(role);
  return !!userRole ? getRoleTranslationKey(userRole) : '';
};

export const getWorkspaceRolesbyUserRoles = (
  roles: Roles[] | string[],
): AdminWorkspaceRoles[] => {
  const useRoles = roles.map((r) => r.toString());
  const userWorkspaceRoles = Object.values(AdminWorkspaceRoles).filter((r) =>
    useRoles.includes(r.toString()),
  );

  return userWorkspaceRoles.includes(AdminWorkspaceRoles.EMPLOYEE)
    ? userWorkspaceRoles
    : [...userWorkspaceRoles, AdminWorkspaceRoles.EMPLOYEE];
};

export const getWorkspaceRoleByUserRole = (
  role: Roles,
): AdminWorkspaceRoles => {
  const wsRoles = getWorkspaceRolesbyUserRoles([role]);

  return wsRoles.length > 0 ? wsRoles[0] : AdminWorkspaceRoles.EMPLOYEE;
};

export const getRoleWidgets = (role: Roles): MyWorkspaceWidgetNames[] =>
  getWorkspaceRoleWidgets(getWorkspaceRoleByUserRole(role));

export const getWidgetDataRequestRoles = (
  role?: AdminWorkspaceRoles,
): AdminWorkspaceRoles[] | undefined => {
  return !!role ? [role] : undefined;
};
